import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Ruler, Layers, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const imgUrl = (url, taille = "l") => {
  if (!url) return "";
  const u = url.startsWith("/") ? `${BACKEND}${url}` : url;
  return taille === "m" ? u.replace(/_l\.jpg$/, "_m.jpg") : u;
};

export const specsOf = (r) => [
  r.peau && ["Peau", r.peau],
  r.diametre && ["Diamètre", r.diametre],
  r.forme && ["Forme", r.forme],
  r.lacage && ["Laçage", r.lacage],
  r.finition && ["Finition", r.finition],
  r.annee && ["Année", String(r.annee)],
].filter(Boolean);

export const altOf = (r, photo) => photo?.alt || [`Tambour ${r.titre}`, r.peau && `en peau de ${r.peau.toLowerCase()}`, r.diametre && `de ${r.diametre}`, "réalisé par Entre Les Mondes, Orbe"].filter(Boolean).join(" ");

export const RealisationCard = ({ r, onOpen, index = 0, compact = false }) => {
  const photo = r.photos[0];
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: (index % 3) * 0.08 }}
      className="group" data-testid={`realisation-card-${r.id.slice(0, 8)}`}
    >
      <button type="button" onClick={() => onOpen(r, 0)} className="block w-full text-left" aria-label={`Agrandir ${r.titre}`} data-testid={`realisation-open-${r.id.slice(0, 8)}`}>
        <div className={`relative overflow-hidden rounded-2xl bg-sand-dark ${compact ? "aspect-square" : "aspect-[4/5]"}`}>
          <img src={imgUrl(photo.url, "m")} alt={altOf(r, photo)} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.04]" />
          {r.photos.length > 1 && <span className="absolute top-3 right-3 font-mono text-[10px] tracking-wider text-sand bg-earth/70 backdrop-blur px-2 py-1 rounded-full inline-flex items-center gap-1"><Layers className="w-3 h-3" />{r.photos.length}</span>}
          {r.sur_mesure && <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-earth bg-terra/90 px-3 py-1 rounded-full">Création sur mesure</span>}
        </div>
      </button>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl text-bark leading-tight">{r.titre}</h3>
          {(r.peau || r.diametre) && <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-fumee mt-1.5">{[r.peau, r.diametre].filter(Boolean).join(" · ")}</p>}
        </div>
        {r.lacage && !compact && <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-terra mt-1 shrink-0 hidden sm:inline">{r.lacage}</span>}
      </div>
      {!compact && r.histoire && <p className="text-sm text-fumee leading-relaxed mt-2 line-clamp-2">{r.histoire}</p>}
    </motion.article>
  );
};

export const Lightbox = ({ item, index, onClose, onIndex }) => {
  const photos = item?.photos || [];
  const go = useCallback((d) => onIndex((index + d + photos.length) % photos.length), [index, photos.length, onIndex]);
  useEffect(() => {
    if (!item) return;
    const key = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", key);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = ""; };
  }, [item, go, onClose]);
  const [touchX, setTouchX] = useState(null);
  return createPortal(
    <AnimatePresence>
      {item && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="fixed inset-0 z-[100] bg-earth/95 backdrop-blur-md flex flex-col" role="dialog" aria-modal="true" aria-label={item.titre} data-testid="lightbox" onClick={onClose}>
          <div className="flex items-center justify-between px-5 sm:px-8 py-4 text-sand" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sand/60">{photos.length > 1 ? `${index + 1} / ${photos.length}` : "Tambour réalisé"}</p>
            <button onClick={onClose} aria-label="Fermer" data-testid="lightbox-close" className="w-10 h-10 rounded-full border border-sand/30 flex items-center justify-center hover:bg-sand/10 transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 min-h-0 grid lg:grid-cols-[1fr_360px] gap-6 px-5 sm:px-8 pb-6" onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => setTouchX(e.touches[0].clientX)} onTouchEnd={(e) => { if (touchX != null && Math.abs(e.changedTouches[0].clientX - touchX) > 50) go(e.changedTouches[0].clientX < touchX ? 1 : -1); setTouchX(null); }}>
            <div className="relative min-h-0 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.img key={index} src={imgUrl(photos[index]?.url)} alt={altOf(item, photos[index])} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="max-h-[60vh] lg:max-h-[80vh] max-w-full object-contain rounded-xl shadow-2xl" data-testid="lightbox-image" />
              </AnimatePresence>
              {photos.length > 1 && (
                <>
                  <button onClick={() => go(-1)} aria-label="Photo précédente" data-testid="lightbox-prev" className="absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-sand/10 hover:bg-sand/20 text-sand flex items-center justify-center transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                  <button onClick={() => go(1)} aria-label="Photo suivante" data-testid="lightbox-next" className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-sand/10 hover:bg-sand/20 text-sand flex items-center justify-center transition-colors"><ChevronRight className="w-5 h-5" /></button>
                </>
              )}
            </div>
            <aside className="text-sand overflow-y-auto">
              <p className="eyebrow text-sand/60 mb-3">Tambour réalisé{item.annee ? ` · ${item.annee}` : ""}</p>
              <h2 className="font-serif text-3xl leading-tight">{item.titre}</h2>
              {item.sur_mesure && <p className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-terra"><Sparkles className="w-3.5 h-3.5" />Création sur mesure</p>}
              {item.histoire && <p className="mt-5 text-sand/80 leading-relaxed">{item.histoire}</p>}
              {specsOf(item).length > 0 && (
                <dl className="mt-6 border-t border-sand/15 divide-y divide-sand/15">
                  {specsOf(item).map(([k, v]) => <div key={k} className="flex justify-between gap-6 py-2.5 text-sm"><dt className="text-sand/55 inline-flex items-center gap-2"><Ruler className="w-3.5 h-3.5 opacity-60" />{k}</dt><dd className="text-sand text-right">{v}</dd></div>)}
                </dl>
              )}
              {photos.length > 1 && (
                <div className="mt-6 flex gap-2 flex-wrap">
                  {photos.map((p, i) => <button key={p.url} onClick={() => onIndex(i)} aria-label={`Photo ${i + 1}`} className={`w-14 h-14 rounded-lg overflow-hidden border ${i === index ? "border-terra" : "border-transparent opacity-60 hover:opacity-100"} transition-opacity`}><img src={imgUrl(p.url, "m")} alt="" className="w-full h-full object-cover" /></button>)}
                </div>
              )}
            </aside>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
